import { useEffect, useMemo, useState } from "react";
import { api, money, formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  PencilSimple, Trash, DeviceMobile, Gauge, Cube, Wrench, Check, X,
  ArrowCounterClockwise, Plus, CopySimple, WarningCircle, Eye, EyeSlash,
  DotsSixVertical,
} from "@phosphor-icons/react";
import {
  MANUFACTURERS, DEVICE_TYPES, MODELS, PROBLEMS,
  arrangeProblems, repairHiddenScope, repairOrderScope, resolveRepairLayout, REPAIR_GROUPS,
} from "../data/repairCatalog";
import { useAuth } from "../context/AuthContext";
import { invalidateWizardCatalogCache } from "../lib/catalogCache";

const EMPTY_EDITOR = {
  id: "", type: "device_type", label: "", price: 0, hidden: false,
  scope_mode: "brand", parent_manufacturer: "", parent_device_type: "", parent_model: "",
  confirmDelete: false,
};

const safe = (v) => String(v || "").replace(/[^a-zA-Z0-9_-]/g, "_");
const norm = (v) => String(v || "").trim().toLowerCase();

export default function RepairCatalogTab({ catalog, reloadCatalog, q }) {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [websiteCatalog, setWebsiteCatalog] = useState({ manufacturers: [] });
  const [view, setView] = useState("tree");
  const [selectedBrand, setSelectedBrand] = useState("apple");
  const [selectedDevice, setSelectedDevice] = useState("iphone");
  const [selectedModel, setSelectedModel] = useState("");
  const [editor, setEditor] = useState(null);
  const [saving, setSaving] = useState(false);
  const [selectedProblems, setSelectedProblems] = useState(new Set());
  const [bulkMode, setBulkMode] = useState("model");
  const [cloneSource, setCloneSource] = useState({ brand: "apple", device: "iphone", model: "" });
  // Chain-wide display orders / removals, keyed by scope (see repairCatalog.js)
  const [displayOrders, setDisplayOrders] = useState({});
  const [dragIdx, setDragIdx] = useState(null);
  const [showHidden, setShowHidden] = useState(false);
  const [pendingRemove, setPendingRemove] = useState("");
  // Which slice of the catalog a drag / removal applies to.
  const [layoutScope, setLayoutScope] = useState("model");

  const loadDisplayOrders = () => api.get("/catalog/website/order/all")
    .then((r) => setDisplayOrders(r.data || {}))
    .catch(() => setDisplayOrders({}));

  useEffect(() => { loadDisplayOrders(); }, []);

  useEffect(() => {
    api.get("/catalog/website")
      .then((r) => setWebsiteCatalog(r.data || { manufacturers: [] }))
      .catch(() => setWebsiteCatalog({ manufacturers: [] }));
  }, []);

  const brands = useMemo(() => {
    const map = new Map();
    (websiteCatalog.manufacturers || []).forEach((m) => map.set(m.id, { id: m.id, label: m.name, source: "website" }));
    MANUFACTURERS.forEach((m) => { if (!map.has(m.id)) map.set(m.id, { id: m.id, label: m.label, source: "preset" }); });
    (catalog.manufacturer || []).filter((c) => !c.override_of && !c.hidden).forEach((c) => map.set(`cat_${c.id}`, { id: `cat_${c.id}`, rawId: c.id, label: c.label, source: "custom" }));
    return [...map.values()].filter((b) => b.id !== "other").sort((a, b) => a.label.localeCompare(b.label));
  }, [websiteCatalog, catalog.manufacturer]);

  const brandLabel = (id) => brands.find((b) => b.id === id)?.label || id || "All brands";
  const websiteBrand = (brandId) => (websiteCatalog.manufacturers || []).find((m) => m.id === brandId);

  const devicesForBrand = (brandId) => {
    const map = new Map();
    (websiteBrand(brandId)?.device_types || []).forEach((d) => map.set(d.id, { id: d.id, label: d.name, source: "website" }));
    if (!map.size) DEVICE_TYPES.filter((d) => d.id !== "other").forEach((d) => map.set(d.id, { id: d.id, label: d.label, source: "preset" }));
    (catalog.device_type || [])
      .filter((c) => !c.override_of && !c.hidden)
      .filter((c) => c.parent_manufacturer === brandId || (!c.parent_manufacturer && c.parent_device_type === "all"))
      .forEach((c) => map.set(`cat_${c.id}`, { id: `cat_${c.id}`, rawId: c.id, label: c.label, source: "custom", entry: c }));
    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
  };

  const deviceLabel = (brandId, deviceId) => devicesForBrand(brandId).find((d) => d.id === deviceId)?.label || deviceId || "All devices";
  const websiteDevice = (brandId, deviceId) => (websiteBrand(brandId)?.device_types || []).find((d) => d.id === deviceId);

  const modelsForDevice = (brandId, deviceId) => {
    const map = new Map();
    (websiteDevice(brandId, deviceId)?.models || []).forEach((m) => map.set(m.id, { id: m.id, label: m.name, sub: [m.year, m.model_number].filter(Boolean).join(" · "), source: "website", raw: m }));
    (MODELS[`${brandId}|${deviceId}`] || []).forEach((name) => { if (!map.has(name)) map.set(name, { id: name, label: name, source: "preset" }); });
    (catalog.model || [])
      .filter((c) => !c.override_of && !c.hidden)
      .filter((c) => {
        const mfr = c.parent_manufacturer || "";
        const dev = c.parent_device_type || "";
        if (!mfr && dev === "all") return true;
        return mfr === brandId && (dev === deviceId || dev === "all");
      })
      .forEach((c) => map.set(`cat_${c.id}`, { id: `cat_${c.id}`, rawId: c.id, label: c.label, source: "custom", entry: c }));
    return [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
  };

  const modelLabel = (brandId, deviceId, modelId) => modelsForDevice(brandId, deviceId).find((m) => m.id === modelId)?.label || modelId || "All models";
  const websiteModel = (brandId, deviceId, modelId) => (websiteDevice(brandId, deviceId)?.models || []).find((m) => m.id === modelId);

  const problemsForScope = (brandId, deviceId, modelId) => {
    const map = new Map();
    (websiteModel(brandId, deviceId, modelId)?.repairs || []).forEach((r) => {
      const price = Number(r.part_options?.[0]?.price || 0);
      map.set(`web_${r.id}`, { id: `web_${r.id}`, label: r.name, price, source: "website", rawId: r.id });
    });
    if (!map.size) PROBLEMS.forEach((p) => map.set(`preset_${p.id}`, { id: `preset_${p.id}`, label: p.label, price: p.price, source: "preset" }));
    (catalog.problem || [])
      .filter((c) => !c.override_of && !c.hidden)
      .filter((c) => isProblemVisible(c, brandId, deviceId, modelId))
      .forEach((c) => map.set(`cat_${c.id}`, { id: `cat_${c.id}`, rawId: c.id, label: c.label, price: c.price || 0, source: "custom", entry: c }));
    const alpha = [...map.values()].sort((a, b) => a.label.localeCompare(b.label));
    const layout = resolveRepairLayout(displayOrders, brandId, deviceId, modelId);
    return arrangeProblems(alpha, {
      ...layout,
      isCustom: (x) => x.source === "custom",
      groupOf: (x) => REPAIR_GROUPS[x.source] ?? REPAIR_GROUPS.preset,
    });
  };

  // Rows removed from this model (any scope) — restorable from the panel.
  const hiddenLabels = resolveRepairLayout(displayOrders, selectedBrand, selectedDevice, selectedModel).hiddenLabels;

  // "All devices" writes the chain-wide scope, "this device" every model in it,
  // "this model only" just the selected model.
  const scopeArgs = () => (
    layoutScope === "all" ? ["", "", ""]
      : layoutScope === "device" ? [selectedBrand, selectedDevice, ""]
        : [selectedBrand, selectedDevice, selectedModel]
  );
  const scopeName = () => (
    layoutScope === "all" ? "all devices"
      : layoutScope === "device" ? `every ${deviceLabel(selectedBrand, selectedDevice)} model`
        : modelLabel(selectedBrand, selectedDevice, selectedModel)
  );

  const putScope = async (scope, order) => {
    setDisplayOrders((prev) => ({ ...prev, [scope]: order }));
    await api.put("/catalog/website/order", { scope, order });
  };

  const removeSyncedRepair = async (problem) => {
    setPendingRemove("");
    const scope = repairHiddenScope(...scopeArgs());
    const current = displayOrders[scope] || [];
    try {
      await putScope(scope, [...current.filter((l) => l !== problem.label), problem.label]);
      invalidateWizardCatalogCache();
      toast.success(`Removed "${problem.label}" from ${scopeName()}`);
    } catch (e) {
      toast.error(formatApiError(e) || "Couldn't save");
      loadDisplayOrders();
    }
  };

  // Restore clears the label from every level so it comes back for good.
  const restoreSyncedRepair = async (label) => {
    const scopes = [
      repairHiddenScope("", "", ""),
      repairHiddenScope(selectedBrand, selectedDevice, ""),
      repairHiddenScope(selectedBrand, selectedDevice, selectedModel),
    ];
    try {
      for (const scope of scopes) {
        const current = displayOrders[scope] || [];
        if (current.includes(label)) await putScope(scope, current.filter((l) => l !== label));
      }
      invalidateWizardCatalogCache();
      toast.success(`Restored "${label}"`);
    } catch (e) {
      toast.error(formatApiError(e) || "Couldn't save");
      loadDisplayOrders();
    }
  };

  // Drag-and-drop ordering. The list (website + preset + custom) is persisted
  // as labels so the ticket wizard shows the exact same order.
  const saveProblemOrder = async (labels) => {
    const scope = repairOrderScope(...scopeArgs());
    try {
      await putScope(scope, labels);
      // A broader order would be masked by a narrower one left over from
      // before, so clear the narrower levels when applying wide.
      if (layoutScope === "all") {
        for (const key of Object.keys(displayOrders)) {
          if (key.startsWith("repairorder:") && key !== scope && (displayOrders[key] || []).length) {
            await putScope(key, []);
          }
        }
      } else if (layoutScope === "device") {
        const prefix = `repairorder:${selectedBrand}:${selectedDevice}:`;
        for (const key of Object.keys(displayOrders)) {
          if (key.startsWith(prefix) && key !== scope && (displayOrders[key] || []).length) {
            await putScope(key, []);
          }
        }
      }
      invalidateWizardCatalogCache();
      toast.success(`Order saved for ${scopeName()}`);
    } catch (e) {
      toast.error(formatApiError(e) || "Couldn't save order");
      loadDisplayOrders();
    }
  };

  const onProblemDrop = (toIdx) => {
    if (dragIdx === null || dragIdx === toIdx) { setDragIdx(null); return; }
    const labels = problems.map((p) => p.label);
    const [moved] = labels.splice(dragIdx, 1);
    labels.splice(toIdx, 0, moved);
    setDragIdx(null);
    saveProblemOrder(labels);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const devices = useMemo(() => devicesForBrand(selectedBrand), [selectedBrand, catalog, websiteCatalog]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const models = useMemo(() => modelsForDevice(selectedBrand, selectedDevice), [selectedBrand, selectedDevice, catalog, websiteCatalog]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const problems = useMemo(() => problemsForScope(selectedBrand, selectedDevice, selectedModel), [selectedBrand, selectedDevice, selectedModel, catalog, websiteCatalog, displayOrders]);

  useEffect(() => {
    if (!devices.find((d) => d.id === selectedDevice)) setSelectedDevice(devices[0]?.id || "");
  }, [devices, selectedDevice]);
  useEffect(() => {
    if (!models.find((m) => m.id === selectedModel)) setSelectedModel(models[0]?.id || "");
  }, [models, selectedModel]);

  const unscoped = useMemo(() => {
    const rows = [];
    (catalog.device_type || []).filter((c) => !c.override_of && !c.hidden && !c.parent_manufacturer && c.parent_device_type !== "all")
      .forEach((c) => rows.push({ type: "device_type", label: c.label, entry: c, reason: "No brand selected" }));
    (catalog.model || []).filter((c) => !c.override_of && !c.hidden && (!c.parent_manufacturer || !c.parent_device_type))
      .forEach((c) => rows.push({ type: "model", label: c.label, entry: c, reason: "No device family selected" }));
    (catalog.problem || []).filter((c) => !c.override_of && !c.hidden && !c.parent_manufacturer && !c.parent_device_type)
      .forEach((c) => rows.push({ type: "problem", label: c.label, entry: c, reason: "Legacy global value needs review" }));
    return rows.filter((r) => !q || r.label.toLowerCase().includes(q.toLowerCase()));
  }, [catalog, q]);

  const filteredBrands = q ? brands.filter((b) => b.label.toLowerCase().includes(q.toLowerCase())) : brands;

  const openEditor = (type, item = null, defaults = {}) => {
    const entry = item?.entry || item || {};
    const scopeMode = type === "device_type"
      ? (entry.parent_manufacturer ? "brand" : "all")
      : type === "model"
        ? (!entry.parent_manufacturer && entry.parent_device_type === "all" ? "all" : "device")
        : (!entry.parent_manufacturer && entry.parent_device_type === "all" ? "all" : entry.parent_model ? "model" : "device");
    setEditor({
      ...EMPTY_EDITOR,
      type,
      // Tree rows use `cat_<id>` as a display/selection key. API mutations
      // must always receive the underlying catalog record ID.
      id: item?.rawId || entry.id || "",
      label: entry.label || item?.label || "",
      price: entry.price || item?.price || 0,
      hidden: !!entry.hidden,
      scope_mode: defaults.scope_mode || scopeMode,
      parent_manufacturer: entry.parent_manufacturer || defaults.parent_manufacturer || selectedBrand,
      parent_device_type: (entry.parent_device_type && entry.parent_device_type !== "all") ? entry.parent_device_type : (defaults.parent_device_type || selectedDevice),
      parent_model: entry.parent_model || defaults.parent_model || (type === "problem" ? selectedModel : ""),
      current_brand_id: defaults.parent_manufacturer || selectedBrand,
      current_device_type_id: defaults.parent_device_type || selectedDevice,
      current_model_id: defaults.parent_model || selectedModel,
      confirmDelete: false,
    });
  };

  const saveEditor = async () => {
    if (!editor?.label?.trim()) return toast.error("Enter a name");
    const payload = scopePayload(editor);
    try {
      setSaving(true);
      if (editor.id) await api.patch(`/repair-catalog/${editor.id}`, payload);
      else await api.post("/repair-catalog", { ...payload, type: editor.type });
      invalidateWizardCatalogCache();
      toast.success(editor.id ? "Catalog item updated" : "Catalog item added");
      setEditor(null);
      setSelectedProblems(new Set());
      await reloadCatalog();
    } catch (e) {
      toast.error(formatApiError(e) || "Couldn't save catalog item");
    } finally { setSaving(false); }
  };

  const deleteEditor = async () => {
    if (!editor?.id || !isAdmin) return;
    try {
      setSaving(true);
      await api.delete(`/repair-catalog/${editor.id}`);
      invalidateWizardCatalogCache();
      toast.success(`Deleted custom ${editor.type.replace("_", " ")} from catalog`);
      setEditor(null);
      setSelectedProblems(new Set());
      await reloadCatalog();
    } catch (e) { toast.error(formatApiError(e) || "Couldn't delete"); }
    finally { setSaving(false); }
  };

  const applyBulkScope = async () => {
    const ids = [...selectedProblems];
    if (!ids.length) return toast.error("Select at least one custom issue");
    const patch = scopePayload({
      type: "problem", label: "bulk", price: 0, scope_mode: bulkMode,
      parent_manufacturer: selectedBrand, parent_device_type: selectedDevice, parent_model: selectedModel,
    });
    delete patch.label; delete patch.price;
    try {
      setSaving(true);
      await Promise.all(ids.map((id) => api.patch(`/repair-catalog/${id}`, patch)));
      invalidateWizardCatalogCache();
      toast.success(`Updated ${ids.length} issue${ids.length === 1 ? "" : "s"}`);
      setSelectedProblems(new Set());
      await reloadCatalog();
    } catch (e) { toast.error(formatApiError(e) || "Couldn't update selected issues"); }
    finally { setSaving(false); }
  };

  const cloneRepairs = async () => {
    if (!selectedBrand || !selectedDevice || !selectedModel) return toast.error("Choose the target model first");
    if (!cloneSource.brand || !cloneSource.device || !cloneSource.model) return toast.error("Choose a source model to clone from");
    const source = problemsForScope(cloneSource.brand, cloneSource.device, cloneSource.model);
    const existing = new Set(problemsForScope(selectedBrand, selectedDevice, selectedModel).map((p) => norm(p.label)));
    const toClone = source.filter((p) => !existing.has(norm(p.label)));
    if (!toClone.length) return toast.info("Target model already has these repairs");
    try {
      setSaving(true);
      await Promise.all(toClone.map((p) => api.post("/repair-catalog", {
        type: "problem", label: p.label, price: Number(p.price || 0),
        parent_manufacturer: selectedBrand, parent_device_type: selectedDevice, parent_model: selectedModel,
      })));
      invalidateWizardCatalogCache();
      toast.success(`Cloned ${toClone.length} repair${toClone.length === 1 ? "" : "s"}`);
      await reloadCatalog();
    } catch (e) { toast.error(formatApiError(e) || "Couldn't clone repairs"); }
    finally { setSaving(false); }
  };

  const assignUnscoped = async (row, mode) => {
    const patch = mode === "global"
      ? { parent_manufacturer: "", parent_device_type: "all", parent_model: "" }
      : row.type === "device_type"
        ? { parent_manufacturer: selectedBrand, parent_device_type: "", parent_model: "" }
        : row.type === "model"
          ? { parent_manufacturer: selectedBrand, parent_device_type: selectedDevice, parent_model: "" }
          : { parent_manufacturer: selectedBrand, parent_device_type: selectedDevice, parent_model: selectedModel || "" };
    try {
      await api.patch(`/repair-catalog/${row.entry.id}`, patch);
      invalidateWizardCatalogCache();
      toast.success("Scope fixed");
      await reloadCatalog();
    } catch (e) { toast.error(formatApiError(e) || "Couldn't fix scope"); }
  };

  return (
    <div className="space-y-4" data-testid="repair-catalog-tree-manager">
      <div className="bg-white border border-[#E5E7EB] rounded-[20px] overflow-hidden">
        <div className="p-3 border-b border-[#E5E7EB] flex items-center justify-between gap-3 flex-wrap">
          <div className="inline-flex items-center gap-1 p-1 rounded-full bg-[#F3F4F6]" data-testid="repair-catalog-view-tabs">
            {[{ id: "tree", label: "Tree manager" }, { id: "cleanup", label: `Review unscoped (${unscoped.length})` }].map((t) => (
              <button key={t.id} type="button" onClick={() => setView(t.id)} data-testid={`repair-catalog-view-${t.id}`} className={`h-9 px-3.5 rounded-full text-[12px] font-semibold transition-all ${view === t.id ? "bg-white text-[#121216] shadow-sm" : "text-[#4B5563] hover:text-[#121216]"}`}>{t.label}</button>
            ))}
          </div>
          <div className="text-[11px] text-slate-500" data-testid="repair-catalog-active-path">{brandLabel(selectedBrand)} → {deviceLabel(selectedBrand, selectedDevice)} → {modelLabel(selectedBrand, selectedDevice, selectedModel)}</div>
        </div>

        {view === "cleanup" ? (
          <CleanupPanel rows={unscoped} selectedPath={{ brand: brandLabel(selectedBrand), device: deviceLabel(selectedBrand, selectedDevice), model: modelLabel(selectedBrand, selectedDevice, selectedModel) }} onAssign={assignUnscoped} onEdit={(row) => openEditor(row.type, row.entry)} />
        ) : (
          <div className="grid grid-cols-1 xl:grid-cols-[260px_300px_1fr] min-h-[620px]">
            <TreeColumn title="Brands" icon={Wrench} actionLabel="Add brand" testid="catalog-brand-column" onAdd={() => openEditor("manufacturer", null, { scope_mode: "all", parent_manufacturer: "" })}>
              {filteredBrands.map((b) => <TreeButton key={b.id} item={b} active={selectedBrand === b.id} testid={`catalog-brand-${safe(b.id)}`} onClick={() => { setSelectedBrand(b.id); setSelectedDevice(""); setSelectedModel(""); }} onEdit={b.source === "custom" ? () => openEditor("manufacturer", b) : null} />)}
            </TreeColumn>

            <TreeColumn title="Device families" icon={DeviceMobile} actionLabel="Add device" testid="catalog-device-column" onAdd={() => openEditor("device_type", null, { scope_mode: "brand", parent_manufacturer: selectedBrand })}>
              {devices.map((d) => <TreeButton key={d.id} item={d} active={selectedDevice === d.id} testid={`catalog-device-${safe(d.id)}`} badge={scopeLabel("device_type", d.entry, { brandLabel })} onClick={() => { setSelectedDevice(d.id); setSelectedModel(""); }} onEdit={d.source === "custom" ? () => openEditor("device_type", d) : null} />)}
            </TreeColumn>

            <div className="min-w-0 grid grid-cols-1 lg:grid-cols-[280px_1fr] border-t xl:border-t-0 xl:border-l border-[#E5E7EB]">
              <TreeColumn title="Models" icon={Cube} actionLabel="Add model" testid="catalog-model-column" onAdd={() => openEditor("model", null, { scope_mode: "device", parent_manufacturer: selectedBrand, parent_device_type: selectedDevice })}>
                {models.map((m) => <TreeButton key={m.id} item={m} active={selectedModel === m.id} testid={`catalog-model-${safe(m.id)}`} badge={scopeLabel("model", m.entry, { brandLabel, deviceLabel: () => deviceLabel(selectedBrand, m.entry?.parent_device_type || selectedDevice) })} onClick={() => setSelectedModel(m.id)} onEdit={m.source === "custom" ? () => openEditor("model", m) : null} />)}
              </TreeColumn>

              <div className="min-w-0 border-t lg:border-t-0 lg:border-l border-[#E5E7EB] bg-[#FAFBFC]">
                <div className="p-4 border-b border-[#E5E7EB] flex items-center justify-between gap-3 flex-wrap">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Repairs / problems</div>
                    <h3 className="text-lg font-bold text-[#121216]" data-testid="catalog-problem-title">{modelLabel(selectedBrand, selectedDevice, selectedModel)}</h3>
                  </div>
                  <div className="flex items-center gap-2 flex-wrap">
                  {isAdmin && (
                    <div className="flex items-center gap-1 pr-1" data-testid="catalog-layout-scope">
                      <span className="text-[10px] font-bold uppercase tracking-[0.12em] text-slate-400 mr-1" title="Website repairs stay above custom, custom above presets — drag reorders within each group">Apply to</span>
                      {[
                        { id: "all", label: "All devices" },
                        { id: "device", label: "This device" },
                        { id: "model", label: "This model" },
                      ].map((m) => (
                        <button key={m.id} type="button" onClick={() => setLayoutScope(m.id)} data-testid={`catalog-layout-scope-${m.id}`}
                          className={`h-8 px-2.5 rounded-full text-[11px] font-bold transition-colors ${layoutScope === m.id ? "bg-[#1E5FEB] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>
                          {m.label}
                        </button>
                      ))}
                    </div>
                  )}
                  {hiddenLabels.length > 0 && (
                    <button type="button" onClick={() => setShowHidden((v) => !v)} data-testid="catalog-toggle-hidden-button" className="h-9 px-3 rounded-xl border border-slate-200 bg-white text-slate-600 hover:border-[#1E5FEB] hover:text-[#1E5FEB] text-xs font-bold inline-flex items-center gap-1.5">
                      {showHidden ? <EyeSlash size={13} weight="bold" /> : <Eye size={13} weight="bold" />} Removed ({hiddenLabels.length})
                    </button>
                  )}
                  <button type="button" onClick={() => openEditor("problem", null, { scope_mode: "model", parent_manufacturer: selectedBrand, parent_device_type: selectedDevice, parent_model: selectedModel })} data-testid="catalog-add-problem-button" className="h-9 px-3 rounded-xl bg-[#1E5FEB] hover:bg-[#1849C8] text-white text-xs font-bold inline-flex items-center gap-1.5"><Plus size={13} weight="bold" /> Add repair</button>
                  </div>
                </div>
                <BulkTools selectedCount={selectedProblems.size} bulkMode={bulkMode} setBulkMode={setBulkMode} onApply={applyBulkScope} cloneSource={cloneSource} setCloneSource={setCloneSource} brands={brands} devicesForBrand={devicesForBrand} modelsForDevice={modelsForDevice} onClone={cloneRepairs} saving={saving} />
                {showHidden && hiddenLabels.length > 0 && (
                  <div className="mx-4 mt-4 rounded-2xl border border-slate-200 bg-white p-3" data-testid="catalog-hidden-panel">
                    <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500 mb-2">Removed from this model</div>
                    <div className="flex flex-wrap gap-2">
                      {hiddenLabels.map((label) => (
                        <div key={label} className="inline-flex items-center gap-2 h-8 pl-3 pr-1.5 rounded-full bg-slate-100 text-[12px] font-semibold text-slate-600" data-testid={`catalog-hidden-${safe(label)}`}>
                          {label}
                          <button type="button" onClick={() => restoreSyncedRepair(label)} data-testid={`catalog-restore-${safe(label)}`} title="Restore" className="h-6 px-2 rounded-full bg-white border border-slate-200 text-[10px] font-bold text-[#1E5FEB] hover:border-[#1E5FEB] inline-flex items-center gap-1">
                            <ArrowCounterClockwise size={10} weight="bold" /> Restore
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="p-4 grid grid-cols-1 2xl:grid-cols-2 gap-2" data-testid="catalog-problem-grid">
                  {problems.map((p, idx) => (
                    <ProblemCard key={p.id} problem={p} index={idx} dragging={dragIdx === idx} isAdmin={isAdmin}
                      onDragStart={() => setDragIdx(idx)}
                      onDragOver={(e) => { if (dragIdx !== null && dragIdx !== idx) e.preventDefault(); }}
                      onDrop={() => onProblemDrop(idx)}
                      onDragEnd={() => setDragIdx(null)}
                      confirmingRemove={pendingRemove === p.id}
                      onAskRemove={() => setPendingRemove(p.id)}
                      onCancelRemove={() => setPendingRemove("")}
                      onRemove={() => removeSyncedRepair(p)}
                      selected={p.source === "custom" && selectedProblems.has(p.rawId)} badge={scopeLabel("problem", p.entry, { brandLabel, deviceLabel: () => deviceLabel(p.entry?.parent_manufacturer || selectedBrand, p.entry?.parent_device_type || selectedDevice), modelLabel: () => modelLabel(p.entry?.parent_manufacturer || selectedBrand, p.entry?.parent_device_type || selectedDevice, p.entry?.parent_model || selectedModel) })} onToggle={() => {
                      if (p.source !== "custom") return;
                      setSelectedProblems((prev) => {
                        const next = new Set(prev);
                        if (next.has(p.rawId)) next.delete(p.rawId); else next.add(p.rawId);
                        return next;
                      });
                    }} onEdit={p.source === "custom" ? () => openEditor("problem", p) : null} />
                  ))}
                  {!problems.length && <div className="p-8 text-sm text-slate-500" data-testid="catalog-no-problems">No repairs for this model yet.</div>}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {editor && <CatalogEditor editor={editor} setEditor={setEditor} saving={saving} isAdmin={isAdmin} brands={brands} devicesForBrand={devicesForBrand} modelsForDevice={modelsForDevice} onSave={saveEditor} onDelete={deleteEditor} onClose={() => setEditor(null)} />}
    </div>
  );
}

function isProblemVisible(c, brandId, deviceId, modelId) {
  const mfr = c.parent_manufacturer || "";
  const dev = c.parent_device_type || "";
  const mdl = c.parent_model || "";
  if (!mfr && dev === "all") return true;
  if (!mfr && dev) return dev === deviceId;
  if (mfr && mfr !== brandId) return false;
  if (dev && dev !== "all" && dev !== deviceId) return false;
  if (mdl && mdl !== modelId) return false;
  return !!mfr || dev === "all";
}

function scopePayload(editor) {
  const base = { label: editor.label.trim(), hidden: !!editor.hidden };
  if (editor.type === "problem") base.price = Number(editor.price || 0);
  if (editor.scope_mode === "all") return { ...base, parent_manufacturer: "", parent_device_type: "all", parent_model: "" };
  if (editor.type === "device_type") return { ...base, parent_manufacturer: editor.parent_manufacturer || "", parent_device_type: "", parent_model: "" };
  if (editor.scope_mode === "device") return { ...base, parent_manufacturer: editor.parent_manufacturer || "", parent_device_type: editor.parent_device_type || "", parent_model: "" };
  return { ...base, parent_manufacturer: editor.parent_manufacturer || "", parent_device_type: editor.parent_device_type || "", parent_model: editor.parent_model || "" };
}

function scopeLabel(type, entry, helpers) {
  if (!entry) return "Synced";
  if (entry.hidden) return "Hidden";
  if (!entry.parent_manufacturer && entry.parent_device_type === "all") return type === "device_type" ? "All brands" : "All devices";
  if (type === "device_type") return entry.parent_manufacturer ? `${helpers.brandLabel(entry.parent_manufacturer)} only` : "Needs review";
  if (type === "model") return entry.parent_manufacturer && entry.parent_device_type ? `All ${helpers.deviceLabel(entry.parent_device_type)}` : "Needs review";
  if (entry.parent_model) return helpers.modelLabel(entry.parent_model);
  if (entry.parent_manufacturer && entry.parent_device_type) return `All ${helpers.deviceLabel(entry.parent_device_type)}`;
  return "Needs review";
}

function TreeColumn({ title, icon: Icon, actionLabel, testid, onAdd, children }) {
  return <section className="min-w-0 border-t first:border-t-0 xl:border-t-0 border-[#E5E7EB] bg-white" data-testid={testid}>
    <div className="p-4 border-b border-[#E5E7EB] flex items-center justify-between gap-2">
      <div className="flex items-center gap-2"><Icon size={16} weight="duotone" className="text-[#1E5FEB]" /><h3 className="text-sm font-bold text-[#121216]" data-testid={`${testid}-title`}>{title}</h3></div>
      <button type="button" onClick={onAdd} data-testid={`${testid}-add-button`} className="h-8 px-2.5 rounded-lg border border-[#E5E7EB] hover:border-[#1E5FEB] hover:text-[#1E5FEB] text-[11px] font-bold inline-flex items-center gap-1"><Plus size={12} weight="bold" /> {actionLabel}</button>
    </div>
    <div className="p-3 space-y-2 max-h-[540px] overflow-auto">{children}</div>
  </section>;
}

function TreeButton({ item, active, testid, badge, onClick, onEdit }) {
  return <div className="relative group" data-testid={`${testid}-wrap`}>
    <button type="button" onClick={onClick} data-testid={testid} className={`w-full text-left rounded-xl border p-3 pr-10 transition-all ${active ? "border-[#1E5FEB] bg-blue-50 ring-2 ring-blue-100" : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50"}`}>
      <div className="text-sm font-bold text-slate-900" data-testid={`${testid}-label`}>{item.label}</div>
      <div className="mt-1 flex items-center gap-1.5 flex-wrap">
        <SourceChip source={item.source} testid={`${testid}-source`} />
        {badge && <ScopeChip label={badge} testid={`${testid}-scope`} />}
      </div>
    </button>
    {onEdit && <button type="button" onClick={onEdit} data-testid={`${testid}-edit-button`} title="Edit scope" className="absolute top-2 right-2 h-7 w-7 rounded-lg border border-slate-200 bg-white text-slate-500 hover:border-[#1E5FEB] hover:text-[#1E5FEB] flex items-center justify-center"><PencilSimple size={12} weight="bold" /></button>}
  </div>;
}

function ProblemCard({
  problem, selected, badge, onToggle, onEdit, isAdmin,
  dragging, onDragStart, onDragOver, onDrop, onDragEnd,
  confirmingRemove, onAskRemove, onCancelRemove, onRemove,
}) {
  return <div
    draggable={!!isAdmin}
    onDragStart={onDragStart}
    onDragOver={onDragOver}
    onDrop={onDrop}
    onDragEnd={onDragEnd}
    data-dragging={dragging ? "true" : "false"}
    className={`rounded-xl border bg-white p-3 transition-all ${dragging ? "opacity-40 border-[#1E5FEB]" : selected ? "border-[#1E5FEB] ring-2 ring-blue-100" : "border-slate-200 hover:border-slate-300"}`}
    data-testid={`catalog-problem-${safe(problem.id)}`}
  >
    {confirmingRemove && <div className="mb-2 rounded-lg bg-rose-50 border border-rose-200 p-2 flex items-center justify-between gap-2" data-testid={`catalog-problem-${safe(problem.id)}-remove-confirm`}>
      <span className="text-[11px] font-semibold text-rose-800">Remove from this model?</span>
      <span className="flex gap-1.5">
        <button type="button" onClick={onRemove} data-testid={`catalog-problem-${safe(problem.id)}-remove-yes`} className="h-7 px-2.5 rounded-lg bg-rose-600 text-white text-[11px] font-bold">Remove</button>
        <button type="button" onClick={onCancelRemove} data-testid={`catalog-problem-${safe(problem.id)}-remove-no`} className="h-7 px-2.5 rounded-lg border border-rose-200 bg-white text-rose-700 text-[11px] font-bold">Keep</button>
      </span>
    </div>}
    <div className="flex items-start gap-3">
      {isAdmin && <span className="mt-0.5 text-slate-300 cursor-grab active:cursor-grabbing shrink-0" title="Drag to reorder" data-testid={`catalog-problem-${safe(problem.id)}-drag-handle`}><DotsSixVertical size={16} weight="bold" /></span>}
      <button type="button" onClick={onToggle} disabled={problem.source !== "custom"} data-testid={`catalog-problem-${safe(problem.id)}-select`} className={`mt-0.5 h-5 w-5 rounded-md flex items-center justify-center shrink-0 ${selected ? "bg-[#1E5FEB] text-white" : "border border-slate-300 text-transparent disabled:bg-slate-100"}`}><Check size={12} weight="bold" /></button>
      <div className="min-w-0 flex-1">
        <div className="font-bold text-sm text-slate-900" data-testid={`catalog-problem-${safe(problem.id)}-label`}>{problem.label}</div>
        <div className="mt-1 flex items-center gap-1.5 flex-wrap"><SourceChip source={problem.source} testid={`catalog-problem-${safe(problem.id)}-source`} /><ScopeChip label={badge} testid={`catalog-problem-${safe(problem.id)}-scope`} /></div>
      </div>
      <div className="font-mono text-xs text-slate-600" data-testid={`catalog-problem-${safe(problem.id)}-price`}>${money(problem.price || 0)}</div>
      {onEdit && <button type="button" onClick={onEdit} data-testid={`catalog-problem-${safe(problem.id)}-edit-button`} className="h-8 w-8 rounded-lg border border-slate-200 hover:border-[#1E5FEB] hover:text-[#1E5FEB] text-slate-500 flex items-center justify-center"><PencilSimple size={12} weight="bold" /></button>}
      {/* Synced / preset repairs have no DB row to delete, so removing one
          hides it for this model (and in the ticket wizard). Restorable. */}
      {!onEdit && isAdmin && <button type="button" onClick={onAskRemove} data-testid={`catalog-problem-${safe(problem.id)}-remove-button`} title="Remove from this model" className="h-8 w-8 rounded-lg border border-slate-200 hover:border-rose-300 hover:text-rose-600 text-slate-400 flex items-center justify-center"><Trash size={12} weight="bold" /></button>}
    </div>
  </div>;
}

function BulkTools({ selectedCount, bulkMode, setBulkMode, onApply, cloneSource, setCloneSource, brands, devicesForBrand, modelsForDevice, onClone, saving }) {
  const sourceDevices = cloneSource.brand ? devicesForBrand(cloneSource.brand) : [];
  const sourceModels = cloneSource.brand && cloneSource.device ? modelsForDevice(cloneSource.brand, cloneSource.device) : [];
  return <div className="p-4 border-b border-[#E5E7EB] bg-white grid grid-cols-1 2xl:grid-cols-2 gap-3" data-testid="catalog-bulk-tools">
    <div className="rounded-2xl border border-slate-200 p-3">
      <div className="flex items-center justify-between gap-2"><div className="text-sm font-bold text-slate-900">Bulk assign selected repairs</div><span className="text-[10px] font-mono text-slate-500" data-testid="catalog-bulk-selected-count">{selectedCount} selected</span></div>
      <div className="mt-3 flex items-center gap-2 flex-wrap">
        {[{ id: "all", label: "All devices" }, { id: "device", label: "All in this device" }, { id: "model", label: "This model only" }].map((m) => <button key={m.id} type="button" onClick={() => setBulkMode(m.id)} data-testid={`catalog-bulk-scope-${m.id}`} className={`h-8 px-3 rounded-full text-[11px] font-bold ${bulkMode === m.id ? "bg-[#1E5FEB] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{m.label}</button>)}
        <button type="button" onClick={onApply} disabled={!selectedCount || saving} data-testid="catalog-bulk-apply-button" className="h-8 px-3 rounded-full bg-slate-900 disabled:bg-slate-300 text-white text-[11px] font-bold">Apply</button>
      </div>
    </div>
    <div className="rounded-2xl border border-slate-200 p-3">
      <div className="text-sm font-bold text-slate-900">Clone repairs from another model</div>
      <div className="mt-3 grid grid-cols-1 md:grid-cols-[1fr_1fr_1fr_auto] gap-2">
        <Select value={cloneSource.brand} onChange={(v) => setCloneSource({ brand: v, device: "", model: "" })} options={brands} testid="catalog-clone-brand" />
        <Select value={cloneSource.device} onChange={(v) => setCloneSource((s) => ({ ...s, device: v, model: "" }))} options={sourceDevices} testid="catalog-clone-device" />
        <Select value={cloneSource.model} onChange={(v) => setCloneSource((s) => ({ ...s, model: v }))} options={sourceModels} testid="catalog-clone-model" />
        <button type="button" onClick={onClone} disabled={saving} data-testid="catalog-clone-button" className="h-9 px-3 rounded-xl bg-[#1E5FEB] hover:bg-[#1849C8] disabled:bg-slate-300 text-white text-xs font-bold inline-flex items-center justify-center gap-1"><CopySimple size={13} weight="bold" /> Clone</button>
      </div>
    </div>
  </div>;
}

function CleanupPanel({ rows, selectedPath, onAssign, onEdit }) {
  return <div className="p-4 bg-[#FAFBFC] min-h-[420px]" data-testid="catalog-cleanup-panel">
    <div className="mb-4 rounded-2xl border border-amber-200 bg-amber-50 p-4 flex items-start gap-3"><WarningCircle size={20} weight="duotone" className="text-amber-700" /><div><div className="font-bold text-amber-950">Review unscoped catalog items</div><div className="text-sm text-amber-800" data-testid="catalog-cleanup-context">Current target: {selectedPath.brand} → {selectedPath.device} → {selectedPath.model || "selected model"}</div></div></div>
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
      {rows.map((row) => <div key={row.entry.id} className="rounded-2xl border border-slate-200 bg-white p-4" data-testid={`catalog-cleanup-row-${safe(row.entry.id)}`}>
        <div className="flex items-start justify-between gap-3"><div><div className="text-sm font-bold text-slate-900" data-testid={`catalog-cleanup-label-${safe(row.entry.id)}`}>{row.label}</div><div className="text-xs text-slate-500 mt-1" data-testid={`catalog-cleanup-reason-${safe(row.entry.id)}`}>{row.reason}</div></div><ScopeChip label={row.type.replace("_", " ")} testid={`catalog-cleanup-type-${safe(row.entry.id)}`} /></div>
        <div className="mt-4 flex flex-wrap gap-2"><button type="button" onClick={() => onAssign(row, "current")} data-testid={`catalog-cleanup-assign-current-${safe(row.entry.id)}`} className="h-8 px-3 rounded-lg bg-[#1E5FEB] text-white text-xs font-bold">Assign to current path</button><button type="button" onClick={() => onAssign(row, "global")} data-testid={`catalog-cleanup-assign-global-${safe(row.entry.id)}`} className="h-8 px-3 rounded-lg border border-slate-200 text-slate-600 text-xs font-bold">Make global</button><button type="button" onClick={() => onEdit(row)} data-testid={`catalog-cleanup-edit-${safe(row.entry.id)}`} className="h-8 px-3 rounded-lg border border-slate-200 text-slate-600 text-xs font-bold">Edit</button></div>
      </div>)}
      {!rows.length && <div className="col-span-full p-10 text-center text-sm text-slate-500" data-testid="catalog-cleanup-empty">No unscoped catalog items need review.</div>}
    </div>
  </div>;
}

function CatalogEditor({ editor, setEditor, saving, isAdmin, brands, devicesForBrand, modelsForDevice, onSave, onDelete, onClose }) {
  const update = (patch) => setEditor((e) => ({ ...e, ...patch }));
  const devices = editor.parent_manufacturer ? devicesForBrand(editor.parent_manufacturer) : [];
  const models = editor.parent_manufacturer && editor.parent_device_type ? modelsForDevice(editor.parent_manufacturer, editor.parent_device_type) : [];
  const defaultBrandId = editor.parent_manufacturer || editor.current_brand_id || brands[0]?.id || "";
  const defaultDeviceId = (editor.parent_device_type && editor.parent_device_type !== "all") ? editor.parent_device_type : (editor.current_device_type_id || "");
  const defaultModelId = editor.parent_model || editor.current_model_id || "";
  const scopeOptions = editor.type === "device_type"
    ? [{ id: "all", label: "All brands" }, { id: "brand", label: "Selected brand" }]
    : editor.type === "model"
      ? [{ id: "all", label: "All devices" }, { id: "device", label: "Selected device type" }]
      : [{ id: "all", label: "All devices" }, { id: "device", label: "All models in device type" }, { id: "model", label: "Particular model" }];
  return <div className="fixed inset-0 z-50 bg-slate-950/40 flex justify-end" data-testid="catalog-editor-overlay">
    <aside className="w-full max-w-lg h-full bg-white shadow-2xl flex flex-col" data-testid="catalog-editor-drawer">
      <div className="p-5 border-b border-slate-100 flex items-center justify-between"><div><div className="text-[10px] uppercase tracking-[0.18em] text-[#1E5FEB] font-bold">Tree catalog</div><h3 className="text-xl font-bold text-slate-950" data-testid="catalog-editor-title">{editor.id ? "Edit" : "Add"} {editor.type.replace("_", " ")}</h3></div><button type="button" onClick={onClose} data-testid="catalog-editor-close" className="h-9 w-9 rounded-full border border-slate-200 flex items-center justify-center"><X size={14} weight="bold" /></button></div>
      <div className="flex-1 overflow-auto p-5 space-y-4">
        <label className="block"><Label>Name</Label><input value={editor.label} onChange={(e) => update({ label: e.target.value })} data-testid="catalog-editor-name" className={inputCls} /></label>
        {editor.type === "problem" && <label className="block"><Label>Default price</Label><input type="number" min="0" step="0.01" value={editor.price} onChange={(e) => update({ price: e.target.value })} data-testid="catalog-editor-price" className={inputCls + " font-mono text-right"} /></label>}
        <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 space-y-3" data-testid="catalog-editor-scope-box">
          <div className="text-sm font-bold text-slate-900">Where should it show?</div>
          <div className="grid grid-cols-1 gap-2">{scopeOptions.map((s) => <button key={s.id} type="button" onClick={() => {
            if (s.id === "all") update({ scope_mode: "all", parent_manufacturer: "", parent_device_type: "all", parent_model: "" });
            else if (s.id === "brand") update({ scope_mode: "brand", parent_manufacturer: defaultBrandId, parent_device_type: "", parent_model: "" });
            else if (s.id === "device") update({ scope_mode: "device", parent_manufacturer: defaultBrandId, parent_device_type: defaultDeviceId, parent_model: "" });
            else update({ scope_mode: "model", parent_manufacturer: defaultBrandId, parent_device_type: defaultDeviceId, parent_model: defaultModelId });
          }} data-testid={`catalog-editor-scope-${s.id}`} className={`h-10 rounded-xl border text-sm font-bold ${editor.scope_mode === s.id ? "border-[#1E5FEB] bg-blue-50 text-[#1E5FEB]" : "border-slate-200 bg-white text-slate-600"}`}>{s.label}</button>)}</div>
          {editor.scope_mode !== "all" && <Select label="Brand" value={editor.parent_manufacturer} onChange={(v) => update({ parent_manufacturer: v, parent_device_type: "", parent_model: "" })} options={brands} testid="catalog-editor-brand" />}
          {editor.type !== "device_type" && editor.scope_mode !== "all" && editor.parent_manufacturer && <Select label="Device type" value={editor.parent_device_type} onChange={(v) => update({ parent_device_type: v, parent_model: "" })} options={devices} testid="catalog-editor-device" />}
          {editor.type === "problem" && editor.scope_mode === "model" && editor.parent_device_type && <Select label="Model" value={editor.parent_model} onChange={(v) => update({ parent_model: v })} options={models} testid="catalog-editor-model" />}
          <button type="button" role="switch" aria-checked={!editor.hidden} onClick={() => update({ hidden: !editor.hidden })} data-testid="catalog-editor-visible-toggle" className={`h-9 px-3 rounded-xl text-xs font-bold inline-flex items-center gap-1.5 ${editor.hidden ? "bg-slate-200 text-slate-600" : "bg-emerald-100 text-emerald-700"}`}>{editor.hidden ? <EyeSlash size={13} /> : <Eye size={13} />} {editor.hidden ? "Hidden from wizard" : "Visible in wizard"}</button>
        </div>
        {editor.confirmDelete && <div className="rounded-2xl border border-rose-200 bg-rose-50 p-4" data-testid="catalog-editor-delete-confirm"><div className="text-sm font-bold text-rose-900">Delete this custom {editor.type.replace("_", " ")}?</div><div className="mt-1 text-xs text-rose-800" data-testid="catalog-editor-delete-warning">{editor.type === "manufacturer" ? "Custom devices, models, and repairs under this brand will also be removed." : editor.type === "device_type" ? "Custom models and repairs under this device family will also be removed." : editor.type === "model" ? "Custom repairs under this model will also be removed." : "This custom repair will be removed from future ticket choices."}</div><div className="mt-3 flex gap-2"><button type="button" onClick={onDelete} disabled={saving} data-testid="catalog-editor-delete-confirm-button" className="h-9 px-3 rounded-lg bg-rose-600 text-white text-xs font-bold">Delete</button><button type="button" onClick={() => update({ confirmDelete: false })} data-testid="catalog-editor-delete-cancel-button" className="h-9 px-3 rounded-lg border border-rose-200 bg-white text-rose-700 text-xs font-bold">Keep</button></div></div>}
      </div>
      <div className="p-5 border-t border-slate-100 flex items-center justify-between bg-slate-50">{editor.id && isAdmin ? <button type="button" onClick={() => update({ confirmDelete: true })} disabled={saving} data-testid="catalog-editor-delete-button" className="h-10 px-3 rounded-xl border border-rose-200 text-rose-600 text-xs font-bold inline-flex items-center gap-1"><Trash size={13} /> Delete</button> : <span />}<div className="flex gap-2"><button type="button" onClick={onClose} disabled={saving} data-testid="catalog-editor-cancel-button" className="h-10 px-4 rounded-xl border border-slate-200 text-slate-600 text-xs font-bold">Cancel</button><button type="button" onClick={onSave} disabled={saving} data-testid="catalog-editor-save-button" className="h-10 px-5 rounded-xl bg-[#1E5FEB] text-white text-xs font-bold">{saving ? "Saving…" : "Save"}</button></div></div>
    </aside>
  </div>;
}

function Select({ label, value, onChange, options, testid }) {
  return <label className="block min-w-0">{label && <Label>{label}</Label>}<select value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} className={inputCls}><option value="">Choose…</option>{options.map((o) => <option key={o.id} value={o.id}>{o.label}</option>)}</select></label>;
}

function SourceChip({ source, testid }) {
  const text = source === "website" ? "Website" : source === "preset" ? "Preset" : "Custom";
  return <span data-testid={testid} className={`text-[9px] font-bold uppercase tracking-[0.14em] px-1.5 py-0.5 rounded-full ${source === "custom" ? "bg-blue-100 text-blue-700" : source === "website" ? "bg-emerald-100 text-emerald-700" : "bg-slate-100 text-slate-500"}`}>{text}</span>;
}

function ScopeChip({ label, testid }) {
  const warn = label === "Needs review";
  return <span data-testid={testid} className={`text-[9px] font-bold uppercase tracking-[0.12em] px-1.5 py-0.5 rounded-full ${warn ? "bg-amber-100 text-amber-800" : "bg-slate-100 text-slate-600"}`}>{label}</span>;
}

function Label({ children }) { return <div className="text-[11px] font-bold uppercase tracking-[0.12em] text-slate-500 mb-1" data-testid={`catalog-label-${safe(children)}`}>{children}</div>; }

const inputCls = "w-full h-10 px-3 rounded-xl border border-slate-200 bg-white text-sm font-semibold text-slate-800 focus:border-[#1E5FEB] focus:ring-2 focus:ring-blue-100 focus:outline-none";